"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import posthog from "posthog-js";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useI18n } from "@/locales/client";
import { createClient } from "@/utils/supabase/client";

const formSchema = z.object({
  otp: z.string().min(6, "Please enter a valid verification code"),
});

export const OtpForm = ({
  email,
  onSuccess,
  reset,
}: {
  email: string;
  onSuccess: () => void;
  reset: () => void;
}) => {
  const t = useI18n();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      otp: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const supabase = createClient();
      const {
        data: { session },
        error,
      } = await supabase.auth.verifyOtp({
        email,
        token: values.otp,
        type: "email",
      });

      if (error) {
        form.setError("otp", {
          type: "manual",
          message: error.message,
        });
        return;
      }

      posthog.capture("email_verified", {
        email: session?.user.email,
        userId: session?.user.id,
        method: "email",
      });

      onSuccess();
    } catch (_err) {
      form.setError("otp", {
        type: "manual",
        message: t("auth.failedToVerify"),
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="otp"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormDescription>{t("auth.didntGetEmail")}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col md:flex-row items-center gap-1">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="w-full md:w-fit"
          >
            {form.formState.isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {t("auth.confirmAndContinue")}
          </Button>
          <Button
            onClick={reset}
            className="text-brand-primary"
            variant={"ghost"}
          >
            {t("auth.tryAnotherEmail")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default OtpForm;
